import { ApiRequestError, executeSdkEnvelopeRequest } from './api-request.util';
import type { AdminMutationMessageResult } from './admin-content.service';
import { adminSportTopPlayersApi } from './sdk-client';

export class AdminSportTopPlayersError extends ApiRequestError {
  constructor(
    status: number,
    message: string,
    name = 'AdminSportTopPlayersError',
    errorCode?: string,
  ) {
    if (errorCode) {
      super(status, message, name, errorCode);
    } else {
      super(status, message, name);
    }
  }
}

export type AdminSportTopPlayer = {
  id: string;
  sportId: string;
  playerId: string;
  playerSlug: string;
  playerName: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type CreateSportTopPlayerInput = {
  sport_code: string;
  player_slug: string;
  sort_order: number;
};

export type UpdateSportTopPlayerInput = {
  sort_order: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asDateString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  return '';
}

function parseSportTopPlayer(value: unknown): AdminSportTopPlayer | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  return {
    id: value.id,
    sportId: asString(value['sportId'] ?? value['sport_id']),
    playerId: asString(value['playerId'] ?? value['player_id']),
    playerSlug: asString(value['playerSlug'] ?? value['player_slug']),
    playerName: asString(value['playerName'] ?? value['player_name']),
    sortOrder: asNumber(value['sortOrder'] ?? value['sort_order'], 1),
    createdAt: asDateString(value['createdAt'] ?? value['created_at']),
    updatedAt: asDateString(value['updatedAt'] ?? value['updated_at']),
  };
}

function parseMessageResult(
  json: unknown,
  fallback: string,
): AdminMutationMessageResult {
  if (isRecord(json) && typeof json.message === 'string') {
    return { message: json.message };
  }
  return { message: fallback };
}

export function sportTopPlayerErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!(error instanceof ApiRequestError)) {
    return fallback;
  }

  switch (error.errorCode) {
    case 'SPORT_TOP_PLAYER_EXISTS':
      return error.message || 'That player is already on this sport\'s top list.';
    case 'PLAYER_NOT_FOUND':
      return error.message || 'Player not found in the catalog.';
    case 'PLAYER_SPORT_MISMATCH':
      return (
        error.message || 'That player does not belong to the selected sport.'
      );
    case 'SPORT_NOT_FOUND':
      return error.message || 'Sport not found.';
    case 'SPORT_TOP_PLAYER_NOT_FOUND':
      return error.message || 'Top player row not found.';
    default:
      return error.message || fallback;
  }
}

export async function listSportTopPlayers(
  sportCode: string,
): Promise<AdminSportTopPlayer[]> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminSportTopPlayersApi.adminSportTopPlayersControllerListV1Raw({
        sportCode,
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = Array.isArray(envelope['data'])
        ? envelope['data']
        : Array.isArray(json)
          ? json
          : [];
      return data
        .map(parseSportTopPlayer)
        .filter((item): item is AdminSportTopPlayer => item != null);
    },
    fallback: 'Failed to load top players',
    ErrorClass: AdminSportTopPlayersError,
  });
}

export async function createSportTopPlayer(
  input: CreateSportTopPlayerInput,
): Promise<AdminSportTopPlayer | AdminMutationMessageResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminSportTopPlayersApi.adminSportTopPlayersControllerCreateV1Raw({
        createSportTopPlayerDto: {
          sport_code: input.sport_code,
          player_slug: input.player_slug,
          sort_order: input.sort_order,
        },
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const mapped = parseSportTopPlayer(envelope['data']);
      if (mapped) return mapped;
      return parseMessageResult(json, 'Top player added');
    },
    fallback: 'Failed to add top player',
    ErrorClass: AdminSportTopPlayersError,
  });
}

export async function updateSportTopPlayer(
  id: string,
  input: UpdateSportTopPlayerInput,
): Promise<AdminMutationMessageResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminSportTopPlayersApi.adminSportTopPlayersControllerUpdateV1Raw({
        id,
        updateSportTopPlayerDto: {
          sort_order: input.sort_order,
        },
      }),
    parse: (json) => parseMessageResult(json, 'Top player updated'),
    fallback: 'Failed to update top player',
    ErrorClass: AdminSportTopPlayersError,
  });
}

export async function deleteSportTopPlayer(
  id: string,
): Promise<AdminMutationMessageResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminSportTopPlayersApi.adminSportTopPlayersControllerDeleteV1Raw({
        id,
      }),
    parse: (json) => parseMessageResult(json, 'Top player removed'),
    fallback: 'Failed to remove top player',
    ErrorClass: AdminSportTopPlayersError,
  });
}
